import type { Breadcrumb, Event } from "@sentry/react";

/**
 * A URL with its fragment removed. The fragment is where this app puts secrets
 * that must never reach a server (the claim-link token, the website sign-in
 * capability), and the error tracker runs in the document, where the fragment
 * still exists: it records the current URL on every event and the from/to of
 * every history change, including the `replaceState` that strips the secret.
 */
export function stripUrlFragment(url: string): string {
  const at = url.indexOf("#");
  return at === -1 ? url : url.slice(0, at);
}

/** `beforeBreadcrumb`: navigation breadcrumbs carry from/to URLs. */
export function scrubBreadcrumbUrls(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.category !== "navigation" || !breadcrumb.data) return breadcrumb;
  const data = { ...breadcrumb.data };
  for (const key of ["from", "to"] as const) {
    if (typeof data[key] === "string") data[key] = stripUrlFragment(data[key]);
  }
  return { ...breadcrumb, data };
}

/** `beforeSend` / `beforeSendTransaction`: the event's own request URL. */
export function scrubEventUrls<E extends Event>(event: E): E {
  if (typeof event.request?.url !== "string") return event;
  return { ...event, request: { ...event.request, url: stripUrlFragment(event.request.url) } };
}
